/**
 * #1639 C1 (ADR 0006 amendment 2026-10-21): skyData creates the Entra External ID ("Sign in with
 * email") account for a grant pinned to that issuer. One closed set of states and codes, read by
 * the web (the grant's request row and the Users list), the runner (`runner/accounts/`) and the
 * migration's CHECKs (platform 0057), so the three never disagree.
 *
 * Everything is behind `features.flags.invite_account_creation`, off by default. Off, no request
 * is written and a grant behaves as it did before C1 (the operator script makes the account).
 */

/** The platform feature flag (`features.flags`, the Feature flags tab). Off by default. */
export const ACCOUNT_CREATION_FLAG = "invite_account_creation";

export const ACCOUNT_CREATION_STATUSES = [
  "pending",
  "created",
  "exists",
  "not_configured",
  "failed",
  "refused",
  "rate_limited",
] as const;
export type AccountCreationStatus = (typeof ACCOUNT_CREATION_STATUSES)[number];

/**
 * Why a request ended as it did. `failed`: `permission_missing` (Graph or the token endpoint
 * refused the writer, 401 or 403, or the token request was rejected), `address_refused` (Graph
 * refused the body, a 400 other than the identity conflict), `unavailable` (429, 5xx, a timeout
 * or a Key Vault sign failure, after `ACCOUNT_CREATION_MAX_ATTEMPTS`). `refused`: `not_eligible`
 * (the runner's re-check failed). `not_configured`: `flag_off`, `writer_missing` (no writer
 * settings on this runner) or `issuer_not_written` (the pin is not the writer's tenant).
 * `rate_limited` (written closed by the web at grant, never claimed by the runner):
 * `deployment_limit` (the deployment's daily cap was reached) or `person_limit` (this person and
 * issuer already had `ACCOUNT_CREATION_PERSON_DAILY_LIMIT` requests in 24 hours, none of which
 * made or found the account), or no code at all (review of #1684: a tenant cap met by a bulk
 * Reset sign-in, which writes the request closed rather than refusing the whole batch).
 */
export const ACCOUNT_CREATION_CODES = [
  "permission_missing",
  "address_refused",
  "unavailable",
  "not_eligible",
  "flag_off",
  "writer_missing",
  "issuer_not_written",
  "deployment_limit",
  "person_limit",
] as const;
export type AccountCreationCode = (typeof ACCOUNT_CREATION_CODES)[number];

/**
 * Rate limits on new requests, checked in the grant's transaction (ADR 0006 amendment
 * 2026-10-21, decision 1). Rolling windows; a `rate_limited` row is never counted (it costs no
 * Graph call).
 *
 * - Per skyData tenant, `ACCOUNT_CREATION_TENANT_HOURLY_LIMIT` an hour and
 *   `ACCOUNT_CREATION_TENANT_DAILY_LIMIT` a day: over either, the grant is refused 429
 *   `RATE_LIMITED` with the closed detail `ACCOUNT_CREATION_RATE_LIMITED_ISSUE` and nothing is
 *   written. That refusal only hurts the tenant that caused it. The daily cap is well below the
 *   deployment's, so no single tenant can use it up.
 * - Per deployment, `ACCOUNT_CREATION_DEPLOYMENT_DAILY_LIMIT` a day: never refuses the grant
 *   (one tenant's volume must not block every other tenant's grants). The grant commits, the
 *   request is written closed `rate_limited` (`deployment_limit`), its invitation is withdrawn so
 *   it never links to an account that does not exist, and L1 is paged (alert A18).
 * - Per person, issuer and tenant (a platform grant is its own bucket; `refused` closes are not
 *   counted), `ACCOUNT_CREATION_PERSON_DAILY_LIMIT` a day: over it, a grant asks for
 *   nothing when a request of those 24 hours made or found the account, and otherwise writes a
 *   closed `rate_limited` (`person_limit`) request, the invitation withdrawn as above.
 *
 * A platform grant has no tenant and counts against the deployment and person caps only.
 */
export const ACCOUNT_CREATION_TENANT_HOURLY_LIMIT = 50;
export const ACCOUNT_CREATION_TENANT_DAILY_LIMIT = 100;
export const ACCOUNT_CREATION_DEPLOYMENT_DAILY_LIMIT = 500;
export const ACCOUNT_CREATION_PERSON_DAILY_LIMIT = 3;
export const ACCOUNT_CREATION_RATE_LIMITED_ISSUE = "ACCOUNT_CREATION_RATE_LIMITED";
/** Which limit refused a grant (a tenant's) or a Resend (any), for the message and telemetry. */
export type AccountCreationLimitScope = "tenant_hour" | "tenant_day" | "deployment" | "person";

/** Attempts before an `unavailable` request is closed `failed`. */
export const ACCOUNT_CREATION_MAX_ATTEMPTS = 6;

/**
 * Minutes before each retry of an `unavailable` attempt (1st after 1 minute, then 3, 6, 12, 20):
 * the six attempts span about 42 minutes, inside the invitation hold below.
 */
export const ACCOUNT_CREATION_RETRY_MINUTES = [1, 3, 6, 12, 20] as const;

/**
 * How long the grant's invitation waits for the account. The runner releases it as soon as the
 * request closes `created` or `exists` (a revoked grant's never), and withdraws it on
 * `not_configured`, `failed` or `refused` (#1678: no account was made, so no invitation goes out
 * until the admin resends it); the cap only matters when no runner answers at all, and then the
 * invitation goes out as it did before C1.
 */
export const ACCOUNT_INVITE_HOLD_MINUTES = 120;

/** The request's state as the Users list carries it (`AccessUser.accountCreation`). */
export interface AccountCreationView {
  status: AccountCreationStatus;
  code: AccountCreationCode | null;
}

/**
 * #1678: the Users row's line while skyData makes no account here (`not_configured`: no writer,
 * the flag switched off, or a pin the writer does not write). No invitation went out, and nothing
 * sends it on its own: the admin's Resend does, once Skypoint has made the account (review of
 * #1684). It names the person whose account it is, never "you".
 */
export const ACCOUNT_NOT_CONFIGURED_LINE =
  "Skypoint creates this person's sign-in account. Use Resend invitation once Skypoint confirms it exists.";

/**
 * Review of #1684: the `refused` line, true for every cause of `not_eligible` (the person signed
 * in or was reset to another sign-in meanwhile, every grant was revoked, or every grant's tenant
 * was disabled or deleted).
 */
export const ACCOUNT_REFUSED_LINE =
  "This person's access or sign-in changed before the account was made.";

/**
 * Review of #1684: a `rate_limited` request with no code is a TENANT cap met by a bulk Reset sign-in
 * (a single reset, a grant or a Resend is refused 429 instead, and writes nothing).
 */
export const ACCOUNT_TENANT_LIMIT_LINE =
  "Too many new accounts for this tenant. Use Resend invitation later or ask support.";

/**
 * #1678 (#1676): the Users row's line once an invitation went out to an Email person whose
 * account skyData did not make, until their first sign-in (`AccessUser.inviteUnconfirmed`).
 */
export const INVITE_UNCONFIRMED_LINE =
  "Invitation sent. If they cannot sign in, ask Skypoint support to confirm their sign-in account exists.";

/**
 * What the Users row says under the status badge, and the next step when there is one. Customer
 * copy: no semicolon, no dash, full stops (`scripts/check-copy.mjs`). `null` is said nothing: a
 * made account needs no line once the person signs in.
 */
export function accountCreationWords(
  view: AccountCreationView,
): { label: string; next: string | null; tone: "muted" | "danger" } | null {
  switch (view.status) {
    case "pending":
      return { label: "Creating account", next: null, tone: "muted" };
    case "created":
    case "exists":
      return { label: "Sign-in account ready", next: null, tone: "muted" };
    case "not_configured":
      // #1678 G1: no invitation went out. Skypoint makes the account, then the admin resends it.
      return {
        label: "Waiting for account",
        next: ACCOUNT_NOT_CONFIGURED_LINE,
        tone: "muted",
      };
    case "refused":
      return {
        label: "Sign-in account not needed",
        next: ACCOUNT_REFUSED_LINE,
        tone: "muted",
      };
    case "failed":
      return {
        label: "Sign-in account not created",
        next: failedNextStep(view.code),
        tone: "danger",
      };
    case "rate_limited":
      return {
        label: "Sign-in account not created yet",
        next: rateLimitedNextStep(view.code),
        tone: "danger",
      };
    default:
      return null;
  }
}

function rateLimitedNextStep(code: AccountCreationCode | null): string {
  if (code === "person_limit") {
    return "Too many sign-in account requests for this person today. Use Resend invitation tomorrow or ask support.";
  }
  if (code === "deployment_limit") {
    return "Too many new accounts today. Skypoint is notified. Use Resend invitation tomorrow or ask support.";
  }
  return ACCOUNT_TENANT_LIMIT_LINE;
}

function failedNextStep(code: AccountCreationCode | null): string {
  if (code === "address_refused") {
    return "This address cannot be used for email sign-in. Check the address, or grant access again with Microsoft sign-in.";
  }
  if (code === "unavailable") {
    return "The sign-in service did not answer. Revoke and grant again to try once more, or ask Skypoint support.";
  }
  return "Skypoint has been told. Ask Skypoint support to create the account, then resend the invitation.";
}
